/*
  customsirge_kit.js
  Purpose:  The behaviour layer for the customsirge_ section library — a component
            registry plus the handful of interactions every project needs.
  Owns:     window.Customsirge
  Loaded:   in <head> with `defer` (see docs/PORTING.md), before section markup runs.
  Owner:    boilerplate foundation

  ───────────────────────────────────────────────────────────────────────────────
  WHY THIS FILE EXISTS

  Across all four Dawn reference projects the custom JS was the same three
  copy-pasted files (owlandox/customdt-main.js, skinnyroti/customdt-core.js,
  kutftbl/customdt.js) full of global `initFaq()` / `initHeader()` functions that
  ran once on load, bound to global class names, and were never re-run.

  Not one of those themes contained a single `shopify:section:load` listener.

  The consequence is that every custom accordion, carousel and marquee is DEAD in
  the theme editor the moment a merchant adds, duplicates or reorders a section —
  the markup appears, the behaviour does not, and it only comes back on a full
  page reload. Merchants read that as "the theme is broken".

  So: components REGISTER here and the runtime decides when to run them. It runs
  them on first paint, on every section the editor injects, and never twice on the
  same element. It tears them down when a section is removed, so a duplicated
  carousel doesn't leave an orphaned Swiper observing a detached node.

  ───────────────────────────────────────────────────────────────────────────────
  USING IT FROM A SECTION

    Customsirge.register('my-thing', {
      selector: '[data-customsirge-my-thing]',
      init(el, ctx) {
        const onClick = () => {};
        el.addEventListener('click', onClick);
        return () => el.removeEventListener('click', onClick);   // optional cleanup
      },
    });

  `init` receives the matched element and a context object:
    ctx.sectionId  — the Shopify section id, or null outside a section
    ctx.root       — the element scanned (document on load, the section on inject)
    ctx.reason     — 'load' | 'section:load' | 'block:select'

  Return a function to clean up. Everything else is handled: idempotency, editor
  re-init, teardown, and error isolation (one broken component cannot stop the
  others from initialising).
*/

(function () {
  'use strict';

  if (window.Customsirge && window.Customsirge.register) return; // already loaded

  /** name -> { selector, init, onBlockSelect } */
  var components = {};

  /** Marks an element as initialised for a given component, and holds its cleanup. */
  var INSTANCE_KEY = '__customsirgeInstances';

  var kit = {
    /** Debug switch — `Customsirge.debug = true` in the console to trace init. */
    debug: false,

    /**
     * Register a component.
     * @param {string} name
     * @param {{selector: string, init: Function, onBlockSelect?: Function}} spec
     */
    register: function (name, spec) {
      if (!name || !spec || typeof spec.init !== 'function' || !spec.selector) {
        console.error('[customsirge] register("' + name + '") needs { selector, init }');
        return;
      }
      if (components[name]) {
        // Re-registration is a section file being loaded twice. Last one wins,
        // but say so — it usually means a duplicated <script> tag.
        log('component "' + name + '" re-registered');
      }
      components[name] = spec;
      // A component can register after the initial scan (a section's inline
      // script runs after kit.js). Run it immediately over what's on the page.
      if (document.readyState !== 'loading') scanOne(name, document, 'load');
    },

    /** Initialise every registered component inside `root`. */
    scan: function (root, reason) {
      Object.keys(components).forEach(function (name) {
        scanOne(name, root || document, reason || 'load');
      });
    },

    /** Tear down every component instance inside `root`. */
    destroy: function (root) {
      if (!root || !root.querySelectorAll) return;
      var nodes = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
      nodes.forEach(teardown);
    },

    /** Read a breakpoint token so JS and CSS can never disagree. */
    breakpoint: function (name) {
      var raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--customsirge-bp-' + (name || 'md'))
        .trim();
      return parseInt(raw, 10) || (name === 'lg' ? 990 : 750);
    },

    /** True when the viewport is at or above the desktop cut. */
    isDesktop: function () {
      return window.matchMedia('(min-width: ' + kit.breakpoint('md') + 'px)').matches;
    },

    /** True when the visitor asked for less motion. Honour it. */
    prefersReducedMotion: function () {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    /** Wait for a global that a deferred vendor script provides (e.g. Swiper). */
    whenAvailable: function (globalName, timeoutMs) {
      return new Promise(function (resolve, reject) {
        if (window[globalName]) return resolve(window[globalName]);
        var waited = 0;
        var step = 50;
        var limit = timeoutMs || 5000;
        var timer = setInterval(function () {
          if (window[globalName]) {
            clearInterval(timer);
            resolve(window[globalName]);
          } else if ((waited += step) >= limit) {
            clearInterval(timer);
            reject(new Error('[customsirge] ' + globalName + ' never loaded'));
          }
        }, step);
      });
    },
  };

  // ---------------------------------------------------------------------------
  // Runtime
  // ---------------------------------------------------------------------------

  function log() {
    if (!kit.debug) return;
    console.log.apply(console, ['[customsirge]'].concat(Array.prototype.slice.call(arguments)));
  }

  function scanOne(name, root, reason) {
    var spec = components[name];
    if (!spec || !root || !root.querySelectorAll) return;

    var matches = Array.prototype.slice.call(root.querySelectorAll(spec.selector));
    // querySelectorAll only looks at descendants; when the editor hands us a
    // section wrapper the root itself can be the match.
    if (root.matches && root.matches(spec.selector)) matches.unshift(root);

    matches.forEach(function (el) {
      var instances = el[INSTANCE_KEY] || (el[INSTANCE_KEY] = {});
      if (instances[name]) return; // already initialised — idempotency guarantee

      var ctx = {
        sectionId: sectionIdOf(el),
        root: root,
        reason: reason,
      };

      try {
        var cleanup = spec.init(el, ctx);
        instances[name] = typeof cleanup === 'function' ? cleanup : true;
        log('init', name, ctx.sectionId || '(no section)');
      } catch (err) {
        // Isolate failures: one bad component must not block the rest of the page.
        instances[name] = true;
        console.error('[customsirge] "' + name + '" failed to initialise', err, el);
      }
    });
  }

  function teardown(el) {
    var instances = el && el[INSTANCE_KEY];
    if (!instances) return;
    Object.keys(instances).forEach(function (name) {
      var cleanup = instances[name];
      if (typeof cleanup === 'function') {
        try {
          cleanup();
          log('destroy', name);
        } catch (err) {
          console.error('[customsirge] "' + name + '" failed to clean up', err, el);
        }
      }
    });
    delete el[INSTANCE_KEY];
  }

  function sectionIdOf(el) {
    var wrapper = el.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle — first paint plus every theme-editor event.
  // This is the part that was missing from all four reference themes.
  // ---------------------------------------------------------------------------

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  onReady(function () {
    kit.scan(document, 'load');
  });

  // Merchant added, duplicated, or edited a section: its DOM was replaced.
  document.addEventListener('shopify:section:load', function (event) {
    kit.scan(event.target, 'section:load');
  });

  // Merchant removed a section: clean up before the node is discarded.
  document.addEventListener('shopify:section:unload', function (event) {
    kit.destroy(event.target);
  });

  // Merchant reordered sections. The DOM node moves rather than re-renders, so
  // no re-init is needed — but anything measuring layout (carousels, sticky
  // offsets) must recalculate against its new position.
  document.addEventListener('shopify:section:reorder', function (event) {
    window.dispatchEvent(new CustomEvent('customsirge:relayout', { detail: { target: event.target } }));
  });

  // Merchant clicked a block in the editor sidebar — jump the relevant component
  // to that block so the thing they selected is actually visible.
  document.addEventListener('shopify:block:select', function (event) {
    var el = event.target;
    var sectionEl = el.closest('[id^="shopify-section-"]') || document;
    Object.keys(components).forEach(function (name) {
      var spec = components[name];
      if (typeof spec.onBlockSelect !== 'function') return;
      var hosts = Array.prototype.slice.call(sectionEl.querySelectorAll(spec.selector));
      hosts.forEach(function (host) {
        if (!host.contains(el)) return;
        try {
          spec.onBlockSelect(host, el, event);
        } catch (err) {
          console.error('[customsirge] "' + name + '" onBlockSelect failed', err);
        }
      });
    });
  });

  window.Customsirge = kit;

  // ===========================================================================
  // Built-in components
  // ===========================================================================
  // These replace the copy-pasted initFaq/initBlend/initProductAccordion/
  // initHeader family. Each is section-scoped, idempotent and editor-safe.

  /* --- Accordion -----------------------------------------------------------
     Replaces initFaq(). The originals bound a global `.faq-question` click
     handler once at load, so a second FAQ section on the same page shared state
     with the first and neither worked after an editor re-render.

     Uses <details>/<summary> for the semantics (keyboard + screen reader support
     for free) and animates the panel height manually, because `height: auto` is
     not animatable and the reference themes' slideUp/slideDown reimplemented
     jQuery for exactly this.
  */
  kit.register('accordion', {
    selector: '[data-customsirge-accordion]',
    init: function (el) {
      var single = el.getAttribute('data-accordion-single') === 'true';
      var items = Array.prototype.slice.call(el.querySelectorAll('[data-customsirge-accordion-item]'));
      var listeners = [];

      items.forEach(function (item) {
        var summary = item.querySelector('summary');
        var panel = item.querySelector('[data-customsirge-accordion-panel]');
        if (!summary || !panel) return;

        var onClick = function (event) {
          event.preventDefault();
          if (item.open) {
            collapse(item, panel);
          } else {
            if (single) {
              items.forEach(function (other) {
                if (other !== item && other.open) collapse(other, other.querySelector('[data-customsirge-accordion-panel]'));
              });
            }
            expand(item, panel);
          }
        };

        summary.addEventListener('click', onClick);
        listeners.push(function () {
          summary.removeEventListener('click', onClick);
        });

        // Any item rendered open (settings.open_first) needs its panel sized.
        if (item.open) panel.style.height = 'auto';
      });

      return function () {
        listeners.forEach(function (off) {
          off();
        });
      };

      function expand(item, panel) {
        item.open = true;
        if (kit.prefersReducedMotion()) {
          panel.style.height = 'auto';
          return;
        }
        var target = panel.scrollHeight;
        panel.style.height = '0px';
        // force reflow so the browser registers the start value
        void panel.offsetHeight;
        panel.style.height = target + 'px';
        onTransitionEnd(panel, function () {
          if (item.open) panel.style.height = 'auto';
        });
      }

      function collapse(item, panel) {
        if (kit.prefersReducedMotion()) {
          item.open = false;
          return;
        }
        panel.style.height = panel.scrollHeight + 'px';
        void panel.offsetHeight;
        panel.style.height = '0px';
        onTransitionEnd(panel, function () {
          item.open = false;
        });
      }

      function onTransitionEnd(node, fn) {
        var done = function (event) {
          if (event && event.target !== node) return;
          node.removeEventListener('transitionend', done);
          clearTimeout(fallback);
          fn();
        };
        // transitionend does not fire if the property never changes — always
        // arm a fallback or an accordion can wedge shut.
        var fallback = setTimeout(done, 400);
        node.addEventListener('transitionend', done);
      }
    },

    // Editor: clicking an FAQ block in the sidebar opens that question.
    onBlockSelect: function (host, blockEl) {
      var item = blockEl.closest('[data-customsirge-accordion-item]') || blockEl;
      if (item && !item.open) {
        var summary = item.querySelector('summary');
        if (summary) summary.click();
      }
    },
  });

  /* --- Carousel ------------------------------------------------------------
     Wraps Swiper so no section ever calls `new Swiper` directly. The reference
     themes constructed Swiper against global selectors (`.stories-slider`) at
     module scope — meaning a second instance on the page was impossible and an
     editor re-render left a Swiper bound to a detached node.

     Config comes from data attributes so the section's schema settings drive it
     without inline <script> in the section file.
  */
  kit.register('carousel', {
    selector: '[data-customsirge-carousel]',
    init: function (el, ctx) {
      var instance = null;
      var destroyed = false;

      var opts = {
        slidesPerView: num(el.dataset.slidesMobile, 1.15),
        spaceBetween: num(el.dataset.gapMobile, 16),
        loop: el.dataset.loop === 'true',
        autoHeight: el.dataset.autoHeight === 'true',
        a11y: { enabled: true },
        breakpoints: {},
      };

      opts.breakpoints[kit.breakpoint('md')] = {
        slidesPerView: num(el.dataset.slidesDesktop, 3),
        spaceBetween: num(el.dataset.gapDesktop, 24),
      };

      if (el.dataset.slidesWide) {
        opts.breakpoints[kit.breakpoint('lg')] = {
          slidesPerView: num(el.dataset.slidesWide, 4),
          spaceBetween: num(el.dataset.gapDesktop, 24),
        };
      }

      // Autoplay is a design choice, never a default — and it is suppressed
      // outright under prefers-reduced-motion.
      if (el.dataset.autoplay === 'true' && !kit.prefersReducedMotion()) {
        opts.autoplay = { delay: num(el.dataset.autoplayDelay, 5000), disableOnInteraction: false };
      }

      var next = el.querySelector('[data-customsirge-carousel-next]');
      var prev = el.querySelector('[data-customsirge-carousel-prev]');
      if (next && prev) opts.navigation = { nextEl: next, prevEl: prev };

      var pagination = el.querySelector('[data-customsirge-carousel-pagination]');
      if (pagination) opts.pagination = { el: pagination, clickable: true };

      kit
        .whenAvailable('Swiper')
        .then(function (Swiper) {
          if (destroyed) return;
          instance = new Swiper(el, opts);
          el.__customsirgeSwiper = instance;
          // Expose for onBlockSelect and for sections needing slide control.
          el.dispatchEvent(new CustomEvent('customsirge:carousel:ready', { detail: { swiper: instance } }));
        })
        .catch(function (err) {
          // Degrade to a scroll-snap row rather than a broken stack. The CSS
          // fallback in customsirge_kit.css keys off this attribute.
          el.setAttribute('data-customsirge-carousel-fallback', 'true');
          console.warn('[customsirge] carousel', ctx.sectionId, err.message, '— using scroll fallback');
        });

      return function () {
        destroyed = true;
        if (instance && typeof instance.destroy === 'function') instance.destroy(true, true);
        delete el.__customsirgeSwiper;
      };

      function num(value, fallback) {
        var parsed = parseFloat(value);
        return isNaN(parsed) ? fallback : parsed;
      }
    },

    // Editor: clicking a slide block scrolls the carousel to it.
    onBlockSelect: function (host, blockEl) {
      var swiper = host.__customsirgeSwiper;
      if (!swiper) return;
      var slide = blockEl.closest('.swiper-slide');
      if (!slide) return;
      var index = Array.prototype.indexOf.call(slide.parentNode.children, slide);
      if (index > -1) swiper.slideTo(index);
    },
  });

  /* --- Marquee -------------------------------------------------------------
     Infinite horizontal scroll for trust/USP bars. Duplicates its own content
     enough times to fill the viewport, then translates by exactly one copy's
     width — so the loop is seamless at any content length, which the reference
     implementations (fixed 2× duplication) were not on wide screens.
  */
  kit.register('marquee', {
    selector: '[data-customsirge-marquee]',
    init: function (el) {
      var track = el.querySelector('[data-customsirge-marquee-track]');
      if (!track) return;

      var originals = Array.prototype.slice.call(track.children);
      if (!originals.length) return;

      var clones = [];
      var speed = parseFloat(el.dataset.speed) || 40; // px per second

      function build() {
        clones.forEach(function (node) {
          node.remove();
        });
        clones = [];

        // getBoundingClientRect, not offsetWidth: text widths are fractional and
        // offsetWidth rounds each item, so the loop drifted by up to a pixel.
        var unitWidth = originals.reduce(function (sum, node) {
          var style = getComputedStyle(node);
          return (
            sum +
            node.getBoundingClientRect().width +
            parseFloat(style.marginLeft || 0) +
            parseFloat(style.marginRight || 0)
          );
        }, 0);
        if (!unitWidth) return;

        // One copy is N items AND N gaps (the last original's gap is the one
        // before the first clone). Without the gaps the translate stops short
        // and the strip jumps back by N gaps at every loop.
        unitWidth += (parseFloat(getComputedStyle(track).columnGap) || 0) * originals.length;

        // Enough copies to cover the viewport plus a spare to scroll into.
        //
        // floor + 2, NOT ceil + 1. They agree except when viewport/unitWidth is
        // an exact integer, where ceil + 1 lands the last copy exactly on the
        // edge and the loop shows a sliver of white space at the wrap.
        // (Reference-project QA fix 3955e37 — see docs/HANDOFF.md class G.)
        var copies = Math.floor(window.innerWidth / unitWidth) + 2;
        for (var i = 0; i < copies; i++) {
          originals.forEach(function (node) {
            var clone = node.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a, button, input').forEach(function (focusable) {
              focusable.setAttribute('tabindex', '-1');
            });
            track.appendChild(clone);
            clones.push(clone);
          });
        }

        track.style.setProperty('--customsirge-marquee-distance', unitWidth + 'px');
        track.style.setProperty('--customsirge-marquee-duration', unitWidth / speed + 's');

        var wasReady = el.getAttribute('data-customsirge-marquee-ready') === 'true';
        el.setAttribute('data-customsirge-marquee-ready', 'true');

        // On a REBUILD (resize/relayout) the animation is already running and
        // resolved its keyframe translate against the OLD distance. Changing the
        // custom property does not re-resolve a running animation, so it keeps
        // translating by the old width — overshooting into white space, or
        // stopping short and visibly jumping. Restarting the animation makes it
        // resolve against current computed style.
        //
        // Touch only animation-name so the inline duration set above survives.
        // (Reference-project QA fix 3955e37 — see docs/HANDOFF.md class G.)
        if (wasReady) {
          var animationName = getComputedStyle(track).animationName;
          if (animationName && animationName !== 'none') {
            track.style.animationName = 'none';
            void track.offsetWidth; // reflow, so the restart actually takes
            track.style.animationName = animationName;
          }
        }
      }

      var alive = true;

      // Animation is CSS; reduced motion simply never starts it.
      if (!kit.prefersReducedMotion()) {
        build();
        // Init runs at DOMContentLoaded, usually before web fonts arrive. A
        // font swap changes every item's width, so a copy measured in the
        // fallback font loops at the wrong distance: measure again once the
        // fonts are in.
        if (document.fonts && document.fonts.status !== 'loaded') {
          document.fonts.ready.then(function () {
            if (alive) build();
          });
        }
      } else {
        el.setAttribute('data-customsirge-marquee-static', 'true');
      }

      var onResize = debounce(function () {
        if (!kit.prefersReducedMotion()) build();
      }, 200);
      window.addEventListener('resize', onResize);
      window.addEventListener('customsirge:relayout', onResize);

      return function () {
        alive = false;
        window.removeEventListener('resize', onResize);
        window.removeEventListener('customsirge:relayout', onResize);
        clones.forEach(function (node) {
          node.remove();
        });
      };
    },
  });

  /* --- Tabs ---------------------------------------------------------------- */
  kit.register('tabs', {
    selector: '[data-customsirge-tabs]',
    init: function (el) {
      var tabs = Array.prototype.slice.call(el.querySelectorAll('[data-customsirge-tab]'));
      var panels = Array.prototype.slice.call(el.querySelectorAll('[data-customsirge-tab-panel]'));
      if (!tabs.length) return;

      var listeners = [];

      function activate(index) {
        tabs.forEach(function (tab, i) {
          var selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
        });
        panels.forEach(function (panel, i) {
          panel.hidden = i !== index;
        });
      }

      tabs.forEach(function (tab, index) {
        var onClick = function () {
          activate(index);
        };
        // Arrow-key navigation is required for the tablist ARIA pattern.
        var onKeydown = function (event) {
          var offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
          if (!offset) return;
          event.preventDefault();
          var next = (index + offset + tabs.length) % tabs.length;
          activate(next);
          tabs[next].focus();
        };
        tab.addEventListener('click', onClick);
        tab.addEventListener('keydown', onKeydown);
        listeners.push(function () {
          tab.removeEventListener('click', onClick);
          tab.removeEventListener('keydown', onKeydown);
        });
      });

      activate(0);

      return function () {
        listeners.forEach(function (off) {
          off();
        });
      };
    },

    onBlockSelect: function (host, blockEl) {
      var panel = blockEl.closest('[data-customsirge-tab-panel]');
      if (!panel) return;
      var panels = Array.prototype.slice.call(host.querySelectorAll('[data-customsirge-tab-panel]'));
      var index = panels.indexOf(panel);
      var tab = host.querySelectorAll('[data-customsirge-tab]')[index];
      if (tab) tab.click();
    },
  });

  /* --- Reveal on scroll ----------------------------------------------------
     Adds a class when an element enters the viewport. Opt-in per section, and a
     no-op under reduced motion. IntersectionObserver only — no scroll handler.
  */
  kit.register('reveal', {
    selector: '[data-customsirge-reveal]',
    init: function (el) {
      if (kit.prefersReducedMotion() || !('IntersectionObserver' in window)) {
        el.setAttribute('data-customsirge-revealed', 'true');
        return;
      }
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.setAttribute('data-customsirge-revealed', 'true');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
      );
      observer.observe(el);
      return function () {
        observer.disconnect();
      };
    },
  });

  /* --- Sticky bar ----------------------------------------------------------
     Pins an element once its placeholder scrolls out of view. Uses a sentinel +
     IntersectionObserver rather than a scroll listener, so it costs nothing per
     frame. Publishes its height as a custom property so other sections can
     offset against it.
  */
  kit.register('sticky', {
    selector: '[data-customsirge-sticky]',
    init: function (el) {
      var sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;height:1px;width:1px;pointer-events:none;';
      el.parentNode.insertBefore(sentinel, el);

      var observer = new IntersectionObserver(
        function (entries) {
          var stuck = !entries[0].isIntersecting;
          el.setAttribute('data-customsirge-stuck', stuck ? 'true' : 'false');
          document.documentElement.style.setProperty(
            '--customsirge-sticky-height',
            stuck ? el.offsetHeight + 'px' : '0px'
          );
        },
        { threshold: 0 }
      );
      observer.observe(sentinel);

      return function () {
        observer.disconnect();
        sentinel.remove();
        document.documentElement.style.removeProperty('--customsirge-sticky-height');
      };
    },
  });

  /* --- Count up ------------------------------------------------------------
     Animates a stat number when it scrolls into view. Reads the target from the
     rendered text so the merchant's setting stays the source of truth and the
     final value is correct even if JS never runs.
  */
  kit.register('countup', {
    selector: '[data-customsirge-countup]',
    init: function (el) {
      var raw = el.textContent.trim();
      var match = raw.match(/-?[\d.,]+/);
      if (!match || kit.prefersReducedMotion() || !('IntersectionObserver' in window)) return;

      var target = parseFloat(match[0].replace(/,/g, ''));
      if (isNaN(target)) return;

      var prefix = raw.slice(0, match.index);
      var suffix = raw.slice(match.index + match[0].length);
      var decimals = (match[0].split('.')[1] || '').length;
      var duration = parseInt(el.dataset.countupDuration, 10) || 1600;
      var frame = null;

      var observer = new IntersectionObserver(
        function (entries) {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(el);
          var start = performance.now();
          var tick = function (now) {
            var progress = Math.min((now - start) / duration, 1);
            var eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
            if (progress < 1) frame = requestAnimationFrame(tick);
          };
          frame = requestAnimationFrame(tick);
        },
        { threshold: 0.4 }
      );
      observer.observe(el);

      return function () {
        observer.disconnect();
        if (frame) cancelAnimationFrame(frame);
        el.textContent = raw; // restore the merchant's value
      };
    },
  });

  /* --- Before/after comparison slider --------------------------------------
     Replaces the jQuery + zurb-twentytwenty + img-comparison-slider CDN stack
     that kutftbl and skinnyroti loaded on every page (~90KB) to run one section.
     Pointer events cover mouse, touch and pen in one path.
  */
  kit.register('compare', {
    selector: '[data-customsirge-compare]',
    init: function (el) {
      var handle = el.querySelector('[data-customsirge-compare-handle]');
      var start = parseFloat(el.dataset.offset);
      var position = isNaN(start) ? 50 : start;
      var dragging = false;

      set(position);

      var onDown = function (event) {
        dragging = true;
        el.setPointerCapture(event.pointerId);
        move(event);
      };
      var onMove = function (event) {
        if (dragging) move(event);
      };
      var onUp = function (event) {
        dragging = false;
        if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId);
      };
      // Keyboard access — the CDN libraries this replaces had none.
      var onKeydown = function (event) {
        var step = event.key === 'ArrowRight' ? 2 : event.key === 'ArrowLeft' ? -2 : 0;
        if (!step) return;
        event.preventDefault();
        set(Math.min(100, Math.max(0, position + step)));
      };

      el.addEventListener('pointerdown', onDown);
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerup', onUp);
      el.addEventListener('pointercancel', onUp);
      if (handle) handle.addEventListener('keydown', onKeydown);

      return function () {
        el.removeEventListener('pointerdown', onDown);
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerup', onUp);
        el.removeEventListener('pointercancel', onUp);
        if (handle) handle.removeEventListener('keydown', onKeydown);
      };

      function move(event) {
        var rect = el.getBoundingClientRect();
        set(((event.clientX - rect.left) / rect.width) * 100);
      }

      function set(value) {
        position = Math.min(100, Math.max(0, value));
        el.style.setProperty('--customsirge-compare-position', position + '%');
        if (handle) handle.setAttribute('aria-valuenow', Math.round(position));
      }
    },
  });

  // ---------------------------------------------------------------------------
  // Small shared helpers, exposed for section-level scripts.
  // ---------------------------------------------------------------------------

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  kit.debounce = debounce;

  kit.throttle = function (fn, wait) {
    var last = 0;
    var timer;
    return function () {
      var now = Date.now();
      var args = arguments;
      var self = this;
      var remaining = wait - (now - last);
      if (remaining <= 0) {
        last = now;
        fn.apply(self, args);
      } else if (!timer) {
        timer = setTimeout(function () {
          last = Date.now();
          timer = null;
          fn.apply(self, args);
        }, remaining);
      }
    };
  };

  /** Format a Shopify price (cents) using the shop's active format. */
  kit.formatMoney = function (cents, format) {
    var pattern = format || window.Shopify?.money_format || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    return pattern
      .replace(/\{\{\s*amount\s*\}\}/, whole + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, whole);
  };
})();
